// Motion 1.0 — Studio control panel.
// Renders a small segmented control (None · Soft Reveal). Lives in the
// invitation Settings tab; collapsed by default so the editor stays clean.

import React, { useState } from 'react';
import { ChevronDown, ChevronRight, Sparkles } from 'lucide-react';

const SEG = 'inline-flex items-center justify-center px-3 py-1.5 text-[11px] font-medium border border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-300';
const SEG_ON = 'bg-violet-600 text-white border-violet-600 dark:border-violet-500';

export default function MotionPanel({ motionPreset = 'none', onChange }) {
  const [open, setOpen] = useState(false);
  const value = motionPreset === 'soft_reveal' ? 'soft_reveal' : 'none';

  return (
    <div
      className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden"
      data-testid="motion-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-2 px-3 py-2 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50"
        data-testid="motion-panel-toggle"
      >
        <div className="min-w-0 flex items-center gap-2">
          <Sparkles className="w-3.5 h-3.5 text-violet-500 shrink-0" />
          <div>
            <div className="text-xs font-semibold text-gray-900 dark:text-gray-100">Motion</div>
            <div className="text-[11px] text-gray-500 dark:text-gray-400 truncate">
              {value === 'soft_reveal' ? 'Soft reveal · enabled' : 'No entrance animation'}
            </div>
          </div>
        </div>
        {open
          ? <ChevronDown className="w-4 h-4 text-gray-400 shrink-0" />
          : <ChevronRight className="w-4 h-4 text-gray-400 shrink-0" />
        }
      </button>

      {open && (
        <div className="p-3 space-y-3 border-t border-gray-200 dark:border-gray-800">
          <div className="inline-flex rounded-md overflow-hidden" data-testid="motion-segmented">
            <button
              type="button"
              onClick={() => onChange('none')}
              className={[
                SEG,
                value === 'none' ? SEG_ON : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800',
                'rounded-l-md',
              ].join(' ')}
              data-testid="motion-option-none"
            >
              None
            </button>
            <button
              type="button"
              onClick={() => onChange('soft_reveal')}
              className={[
                SEG,
                value === 'soft_reveal' ? SEG_ON : 'bg-white dark:bg-gray-900 hover:bg-gray-50 dark:hover:bg-gray-800',
                '-ml-px rounded-r-md',
              ].join(' ')}
              data-testid="motion-option-soft_reveal"
            >
              Soft Reveal
            </button>
          </div>
          <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed">
            {value === 'soft_reveal'
              ? 'Elements softly appear as guests scroll. Disabled while editing in Studio. Respects "Reduce motion" system preference.'
              : 'No entrance animation. Recommended for existing templates until you preview Soft Reveal on a real phone.'}
          </p>
        </div>
      )}
    </div>
  );
}
